import React, { useState, useEffect } from "react";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Plus,
  BookOpen,
  Building,
  AlertCircle,
  Trash2,
  Video,
  Volume2,
  Image as ImageIcon,
  FileText,
  Code,
  ClipboardCheck,
  Loader2,
  Eye,
  Send,
} from "lucide-react";
import { ApiService } from "../../../../services/api";
import checkPermission from "../../../../utils/permission";
import { useModal } from "../../../../hooks/useModal";
import DepartmentSelector from "../../../../components/selectors/DepartmentSelector";
import LessonContentSelector from "../content/LessonContentSelector";
import Modal from "../../../../components/ui/Modal";

interface AcademicCalendarProps {
  lessonId: string;
  lesson: any;
  onLessonUpdate?: () => void;
}

interface Department {
  id: string;
  name: string;
  parentId: string | null;
}

interface CalendarItem {
  id: string;
  week: number;
  year: number;
  startDate: string;
  endDate: string;
  departmentId: string;
  sectionId: string;
  contentId: string;
  calendarId: string;
  department: Department;
  section: any;
  content: any;
  items: any[];
}

const AcademicCalendar: React.FC<AcademicCalendarProps> = ({ lessonId, lesson, onLessonUpdate }) => {
  const { showAlert, showConfirm, showSelect } = useModal();
  const hasCalendarAddPermission = checkPermission("lesson_calendar_add");
  const hasCalendarDeletePermission = checkPermission("lesson_calendar_delete");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [calendars, setCalendars] = useState<CalendarItem[]>([]);
  const [selectedWeekYear, setSelectedWeekYear] = useState<string>("");
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedDepartmentDetails, setSelectedDepartmentDetails] = useState<Record<string, any>>({});
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [selectedContents, setSelectedContents] = useState<string[]>([]);

  const [expandedWeeks, setExpandedWeeks] = useState<{
    [key: string]: boolean;
  }>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [homeworkSendLoading, setHomeworkSendLoading] = useState<Record<string, boolean>>({});

  const getWeekOptions = () => {
    const options: { value: string; label: string }[] = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentWeek = getCurrentWeek();

    const maxWeekCurrentYear = getMaxWeeksInYear(currentYear);
    const effectiveMaxWeek = Math.max(currentWeek, maxWeekCurrentYear);

    for (let week = currentWeek; week <= effectiveMaxWeek; week++) {
      const startDate = getDateOfWeek(week, currentYear);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);

      const isTransitionWeek = endDate.getFullYear() !== currentYear;
      const label = isTransitionWeek
        ? `${week}. Hafta / 1. Hafta (${formatDate(startDate)} - ${formatDate(endDate)})`
        : `${week}. Hafta (${formatDate(startDate)} - ${formatDate(endDate)})`;

      options.push({
        value: `${currentYear}-${week}`,
        label: label,
      });
    }

    const nextYear = currentYear + 1;
    const startWeekNextYear = currentWeek > maxWeekCurrentYear ? 2 : 1;

    for (let week = startWeekNextYear; week <= 8; week++) {
      const startDate = getDateOfWeek(week, nextYear);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);

      options.push({
        value: `${nextYear}-${week}`,
        label: `${week}. Hafta (${formatDate(startDate)} - ${formatDate(endDate)})`,
      });
    }

    return options;
  };

  const getMaxWeeksInYear = (year: number) => {
    const dec28 = new Date(year, 11, 28);
    const isoWeekStart = getISOWeekStart(year);
    const diffTime = dec28.getTime() - isoWeekStart.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return Math.floor(diffDays / 7) + 1;
  };

  const getISOWeekStart = (year: number) => {
    const simple = new Date(year, 0, 4);

    const dayOfWeek = simple.getDay();
    const ISOWeekStart = new Date(simple);
    ISOWeekStart.setDate(simple.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    return ISOWeekStart;
  };

  const getCurrentWeek = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const isoWeekStart = getISOWeekStart(year);

    const diffTime = currentDate.getTime() - isoWeekStart.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return Math.floor(diffDays / 7) + 1;
  };

  const getDateOfWeek = (week: number, year: number) => {
    const isoWeekStart = getISOWeekStart(year);

    const startDate = new Date(isoWeekStart);
    startDate.setDate(isoWeekStart.getDate() + (week - 1) * 7);

    return startDate;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatWeekRange = (weekNumber: number, year: number) => {
    const startDate = getDateOfWeek(weekNumber, year);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const formatApiDateRange = (startDateStr: string, endDateStr: string) => {
    if (!startDateStr || !endDateStr) return "";

    const startDate = new Date(startDateStr + "T00:00:00");
    const endDate = new Date(endDateStr + "T00:00:00");

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  useEffect(() => {
    loadCalendars();

    const currentYear = new Date().getFullYear();
    const currentWeek = getCurrentWeek();
    setSelectedWeekYear(`${currentYear}-${currentWeek}`);
  }, [lessonId]);

  const loadCalendars = async () => {
    try {
      const response = await ApiService.getLessonCalendars(lessonId);
      setCalendars(response);
    } catch (error) {
      setCalendars([]);
    }
  };

  const handleAddCalendarItem = async () => {
    if (!selectedWeekYear || selectedDepartments.length === 0 || selectedContents.length === 0) {
      setError("Lütfen hafta, departman ve içerik seçiniz");
      return;
    }

    const [yearStr, weekStr] = selectedWeekYear.split('-');
    const year = parseInt(yearStr, 10);
    const week = parseInt(weekStr, 10);

    if (isNaN(year) || isNaN(week)) {
      setError('Geçersiz hafta seçimi');

      return;
    };

    setLoading(true);
    setError("");

    const itemsToAdd: Array<{
      week: number;
      year: number;
      departmentId: string;
      sectionId: string;
      contentId: string;
    }> = [];

    for (const departmentId of selectedDepartments) {
      if (selectedSections.length > 0) {
        for (const sectionId of selectedSections) {
          for (const contentId of selectedContents) {
            itemsToAdd.push({
              week,
              year,
              departmentId,
              sectionId,
              contentId,
            });
          }
        }
      } else {
        for (const contentId of selectedContents) {
          const sectionId = findSectionIdByContentId(contentId);
          if (sectionId) {
            itemsToAdd.push({
              week,
              year,
              departmentId,
              sectionId,
              contentId,
            });
          };
        }
      };
    }

    let successCount = 0;
    let skippedCount = 0;
    let errorCount = 0;

    for (const item of itemsToAdd) {
      try {
        await ApiService.createLessonCalendar(lessonId, item);

        successCount++;
      } catch (error: any) {
        const errorMessage = error?.message || "";

        if (errorMessage.includes("zaten eklenmiş") || errorMessage.includes("already added")) skippedCount++;
        else errorCount++;
      }
    }

    await loadCalendars();

    if (successCount > 0 && skippedCount === 0 && errorCount === 0) {
      await showAlert({
        title: "Başarılı",
        message: `${successCount} takvim öğesi başarıyla eklendi.`,
        type: "success",
      });

      setIsModalOpen(false);
      resetForm();
    } else if (successCount > 0 && skippedCount > 0 && errorCount === 0) {
      await showAlert({
        title: "Kısmen Başarılı",
        message: `${successCount} öğe eklendi, ${skippedCount} öğe zaten mevcut olduğu için atlandı.`,
        type: "warning",
      });

      setIsModalOpen(false);
      resetForm();
    } else if (successCount === 0 && skippedCount > 0 && errorCount === 0) {
      await showAlert({
        title: "Bilgi",
        message: `Seçilen tüm öğeler (${skippedCount}) zaten takvimde mevcut.`,
        type: "info",
      });
    } else if (errorCount > 0) {
      setError(`${errorCount} öğe eklenirken hata oluştu. ${successCount > 0 ? `${successCount} öğe başarıyla eklendi.` : ""} ${skippedCount > 0 ? `${skippedCount} öğe zaten mevcuttu.` : ""}`);
    } else {
      setIsModalOpen(false);
      resetForm();
    };

    setLoading(false);
  };

  const getContentTypeBadgeClass = (type: string) => {
    switch ((type || "").toUpperCase()) {
      case "VIDEO":
        return "bg-purple-100 text-purple-700";
      case "AUDIO":
        return "bg-blue-100 text-blue-700";
      case "IMAGE":
        return "bg-green-100 text-green-700";
      case "TEXT":
        return "bg-gray-100 text-gray-700";
      case "FILE":
      case "PDF":
        return "bg-red-100 text-red-700";
      case "CODE":
        return "bg-yellow-100 text-yellow-700";
      case "TEST":
      case "EXAM":
        return "bg-emerald-100 text-emerald-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return <Video className="w-4 h-4 sm:w-5 sm:h-5" />;
      case "AUDIO":
        return <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />;
      case "IMAGE":
        return <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5" />;
      case "TEXT":
        return <FileText className="w-4 h-4 sm:w-5 sm:h-5" />;
      case "FILE":
      case "PDF":
        return <FileText className="w-4 h-4 sm:w-5 sm:h-5" />;
      case "CODE":
        return <Code className="w-4 h-4 sm:w-5 sm:h-5" />;
      case "TEST":
      case "EXAM":
        return <ClipboardCheck className="w-4 h-4 sm:w-5 sm:h-5" />;
      default:
        return <FileText className="w-4 h-4 sm:w-5 sm:h-5" />;
    }
  };

  const getContentTypeName = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return "Video";
      case "AUDIO":
        return "Ses";
      case "IMAGE":
        return "Resim";
      case "TEXT":
        return "Metin";
      case "FILE":
        return "Dosya";
      case "PDF":
        return "PDF";
      case "CODE":
        return "Kod";
      case "TEST":
      case "EXAM":
        return "Test";
      default:
        return "İçerik";
    }
  };

  const resetForm = () => {
    const currentYear = new Date().getFullYear();
    const currentWeek = getCurrentWeek();

    setSelectedWeekYear(`${currentYear}-${currentWeek}`);
    setSelectedDepartments([]);
    setSelectedDepartmentDetails({});
    setSelectedSections([]);
    setSelectedContents([]);
    setError("");
  };

  const openModal = async () => {
    resetForm();

    if (onLessonUpdate) {
      await onLessonUpdate();
    }

    setIsModalOpen(true);
  };

  const handleDepartmentAdd = (departmentId: string, department?: any) => {
    if (departmentId && department && !selectedDepartments.includes(departmentId)) {
      setSelectedDepartments((prev) => [...prev, departmentId]);
      setSelectedDepartmentDetails((prev) => ({
        ...prev,
        [departmentId]: department,
      }));
    }
  };

  const handleRemoveDepartment = (departmentId: string) => {
    setSelectedDepartments((prev) => prev.filter((id) => id !== departmentId));
    setSelectedDepartmentDetails((prev) => {
      const newDetails = { ...prev };
      delete newDetails[departmentId];
      return newDetails;
    });
  };

  const handleSectionSelection = (sectionId: string) => {
    const section = lesson?.sections?.find((s: any) => s.id === sectionId);
    const sectionContents = section?.contents || [];
    const contentIds = sectionContents.map((c: any) => c.id);

    setSelectedSections((prev) => {
      if (prev.includes(sectionId)) {
        setSelectedContents((prevContents) => prevContents.filter((id) => !contentIds.includes(id)));
        return prev.filter((id) => id !== sectionId);
      } else {
        setSelectedContents((prevContents) => {
          const newContents = [...prevContents];
          contentIds.forEach((id: string) => {
            if (!newContents.includes(id)) {
              newContents.push(id);
            }
          });
          return newContents;
        });
        return [...prev, sectionId];
      }
    });
  };

  const handleContentSelection = (contentId: string) => {
    setSelectedContents((prev) => {
      const newSelectedContents = prev.includes(contentId) ? prev.filter((id) => id !== contentId) : [...prev, contentId];

      const sectionId = findSectionIdByContentId(contentId);
      if (sectionId) {
        const section = lesson?.sections?.find((s: any) => s.id === sectionId);
        const sectionContentIds = section?.contents?.map((c: any) => c.id) || [];

        const selectedSectionContentCount = sectionContentIds.filter((id: string) => newSelectedContents.includes(id)).length;

        setSelectedSections((prevSections) => {
          if (selectedSectionContentCount === 0) {
            return prevSections.filter((id) => id !== sectionId);
          } else if (selectedSectionContentCount === sectionContentIds.length) {
            return prevSections.includes(sectionId) ? prevSections : [...prevSections, sectionId];
          } else {
            return prevSections.filter((id) => id !== sectionId);
          }
        });
      }

      return newSelectedContents;
    });
  };

  const findSectionIdByContentId = (contentId: string): string | null => {
    if (!lesson?.sections) return null;

    for (const section of lesson.sections) {
      if (section.contents?.some((content: any) => content.id === contentId)) {
        return section.id;
      }
    }
    return null;
  };

  const toggleWeek = (id: string) => {
    setExpandedWeeks((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleDeleteWeek = async (weekId: string) => {
    const confirmed = await showConfirm({
      title: "Haftayı Sil",
      message: "Bu haftayı silmek istediğinize emin misiniz?",
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      await ApiService.deleteLessonCalendar(lessonId, weekId);
      await loadCalendars();
    }
  };

  const handleDeleteItem = async (weekId: string, itemId: string) => {
    const confirmed = await showConfirm({
      title: "Öğeyi Sil",
      message: "Bu öğeyi silmek istediğinize emin misiniz?",
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      await ApiService.deleteLessonCalendarItem(lessonId, weekId, itemId);
      await loadCalendars();
    }
  };

  const getDepartmentGroups = (week: any) => {
    const departments = week.items
      .filter((item: any) => item.department && item.department.id)
      .map((item: any) => ({
        id: item.department.id,
        name: item.department.name,
      }));
    return departments.filter((department: any, index: number, self: any) => self.findIndex((t: any) => t.id === department.id) === index);
  };

  const sendAllWeek = async (weekId: string) => {
    const day = await showSelect({
      title: "Ödev Teslim Süresi",
      message: "Öğrencilerin bu haftanın ödevlerini teslim etmeleri için kaç gün süre vermek istiyorsunuz?",
      options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
    });

    if (!day) return;

    const confirmed = await showConfirm({
      title: "Haftayı Gönder",
      message: "Bu haftayı tüm öğrencilere göndermek istediğinize emin misiniz?",
      confirmText: "Gönder",
      confirmButtonVariant: "primary",
    });

    if (confirmed) {
      setHomeworkSendLoading((prev) => ({ ...prev, [`week-${weekId}`]: true }));
      await ApiService.sendWeekHomeWork({
        calendarId: weekId,
        expDay: day,
      });
      await loadCalendars();
      setHomeworkSendLoading((prev) => ({
        ...prev,
        [`week-${weekId}`]: false,
      }));
    }
  };

  const sendDepartmentHomeWork = async (weekId: string, departmentId: string) => {
    const day = await showSelect({
      title: "Ödev Teslim Süresi",
      message: "Bu departmanın öğrencilerinin ödevlerini teslim etmeleri için kaç gün süre vermek istiyorsunuz?",
      options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
    });

    if (!day) return;

    const confirmed = await showConfirm({
      title: "Departman Ödevini Gönder",
      message: "Bu departmanın tüm öğrencilerine göndermek istediğinize emin misiniz?",
      confirmText: "Gönder",
      confirmButtonVariant: "primary",
    });

    if (confirmed) {
      setHomeworkSendLoading((prev) => ({
        ...prev,
        [`dept-${weekId}-${departmentId}`]: true,
      }));
      try {
        const response = await ApiService.sendWeekHomeWork({
          calendarId: weekId,
          departmentId: departmentId,
          expDay: day,
        });
        await loadCalendars();

        if (response) {
          const { sentCount = 0, skippedCount = 0 } = response;

          let message = "";
          let title = "";
          let type: "success" | "warning" | "info" = "success";

          if (sentCount > 0 && skippedCount === 0) {
            title = "Ödev Gönderildi";
            message = `${sentCount} öğrenciye ödev başarıyla gönderildi.`;
            type = "success";
          } else if (sentCount > 0 && skippedCount > 0) {
            title = "Ödev Kısmen Gönderildi";
            message = `${sentCount} öğrenciye ödev gönderildi. ${skippedCount} öğrenci zaten ödev almış durumda olduğu için atlandı.`;
            type = "warning";
          } else if (sentCount === 0 && skippedCount > 0) {
            title = "Ödev Gönderilemedi";
            message = `Tüm öğrenciler (${skippedCount}) zaten bu ödevi almış durumda.`;
            type = "info";
          } else if (sentCount === 0 && skippedCount === 0) {
            title = "Öğrenci Bulunamadı";
            message = `Gönderilecek öğrenci bulunamadı.`;
            type = "warning";
          } else {
            title = "Ödev Gönderimi";
            message = `Ödev gönderimi tamamlandı.`;
            type = "info";
          }

          await showAlert({
            title: title,
            message: message,
            type: type,
          });
        }
      } catch (error: any) {
        await showAlert({
          title: "Hata",
          message: error?.message || "Departman ödevi gönderilirken bir hata oluştu.",
          type: "error",
        });
      } finally {
        setHomeworkSendLoading((prev) => ({
          ...prev,
          [`dept-${weekId}-${departmentId}`]: false,
        }));
      }
    }
  };

  const sendItemHomeWork = async (weekId: string, itemId: string) => {
    const day = await showSelect({
      title: "Ödev Teslim Süresi",
      message: "Öğrencilerin bu içerik ödevini teslim etmeleri için kaç gün süre vermek istiyorsunuz?",
      options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
    });

    if (!day) return;

    const confirmed = await showConfirm({
      title: "Öğeyi Gönder",
      message: "Bu öğeyi tüm öğrencilere göndermek istediğinize emin misiniz?",
      confirmText: "Gönder",
      confirmButtonVariant: "primary",
    });

    if (confirmed) {
      setHomeworkSendLoading((prev) => ({ ...prev, [`item-${itemId}`]: true }));
      try {
        const response = await ApiService.sendWeekHomeWork({
          calendarId: weekId,
          calendarItemId: itemId,
          expDay: day,
        });
        await loadCalendars();

        if (response) {
          const { sentCount = 0, skippedCount = 0 } = response;

          let message = "";
          let title = "";
          let type: "success" | "warning" | "info" = "success";

          if (sentCount > 0 && skippedCount === 0) {
            title = "Ödev Gönderildi";
            message = `${sentCount} öğrenciye ödev başarıyla gönderildi.`;
            type = "success";
          } else if (sentCount > 0 && skippedCount > 0) {
            title = "Ödev Kısmen Gönderildi";
            message = `${sentCount} öğrenciye ödev gönderildi. ${skippedCount} öğrenci zaten ödev almış durumda olduğu için atlandı.`;
            type = "warning";
          } else if (sentCount === 0 && skippedCount > 0) {
            title = "Ödev Gönderilemedi";
            message = `Tüm öğrenciler (${skippedCount}) zaten bu ödevi almış durumda.`;
            type = "info";
          } else if (sentCount === 0 && skippedCount === 0) {
            title = "Öğrenci Bulunamadı";
            message = `Gönderilecek öğrenci bulunamadı.`;
            type = "warning";
          } else {
            title = "Ödev Gönderimi";
            message = `Ödev gönderimi tamamlandı.`;
            type = "info";
          }

          await showAlert({
            title: title,
            message: message,
            type: type,
          });
        }
      } catch (error: any) {
        await showAlert({
          title: "Hata",
          message: error?.message || "Ödev gönderilirken bir hata oluştu.",
          type: "error",
        });
      } finally {
        setHomeworkSendLoading((prev) => ({
          ...prev,
          [`item-${itemId}`]: false,
        }));
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 sm:mb-8 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600" />
              </div>
              <div className="flex-1 sm:flex-initial">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Akademik Takvim</h2>
                <p className="text-sm sm:text-base text-gray-600 mt-0.5 sm:mt-1">Dersinizin haftalık planlamasını oluşturun ve yönetin.</p>
              </div>
            </div>
            {hasCalendarAddPermission && (
              <button
                onClick={openModal}
                className="bg-green-600 text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow-md w-full sm:w-auto">
                <Plus className="w-4 h-4" />
                <span className="hidden xs:inline">Yeni Takvim Öğesi</span>
                <span className="xs:hidden">Yeni Öğe</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {calendars.length > 0 ? (
        <div className="space-y-4">
          {calendars
            .sort((a, b) => b.year - a.year || b.week - a.week)
            .map((week) => {
              const startYear = parseInt(week.startDate?.split("-")[0] || "0");
              const endYear = parseInt(week.endDate?.split("-")[0] || "0");
              const isTransition = startYear > 0 && endYear > 0 && startYear !== endYear;
              const displayTitle = isTransition
                ? `${week.week}. Hafta / 1. Hafta`
                : `${week.week}. Hafta`;

              return (
                <div
                  key={week.id}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md">
                  <div className="border-b border-gray-200 bg-gray-50 p-3 md:p-4">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-2">
                      <button
                        onClick={() => toggleWeek(week.id)}
                        className="flex items-center gap-2 md:gap-3 text-left hover:text-purple-600 transition-colors w-full md:w-auto">
                        <div className="w-9 h-9 md:w-10 md:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900">{displayTitle}</h3>
                          <p className="text-xs md:text-sm text-gray-600">{formatApiDateRange(week.startDate, week.endDate)}</p>
                        </div>
                        {expandedWeeks[week.id] ? (
                          <ChevronUp className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                        )}
                      </button>
                      <div className="flex items-center gap-2 w-full md:w-auto">
                        {hasCalendarDeletePermission && (
                          <button
                            onClick={() => handleDeleteWeek(week.id)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Haftayı Sil">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          className={`flex-1 md:flex-initial px-3 md:px-4 py-2 rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2 ${homeworkSendLoading[`week-${week.id}`]
                            ? "cursor-not-allowed bg-gray-300 text-gray-500"
                            : "cursor-pointer bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md"
                            }`}
                          onClick={() => sendAllWeek(week.id)}
                          disabled={homeworkSendLoading[`week-${week.id}`]}>
                          {homeworkSendLoading[`week-${week.id}`] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              <span className="hidden md:inline">Haftayı Gönder</span>
                            </>
                          )}
                          {!homeworkSendLoading[`week-${week.id}`] && <span className="md:hidden">Gönder</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                  {expandedWeeks[week.id] && (
                    <div className="p-3 md:p-4">
                      {getDepartmentGroups(week).length > 0 && (
                        <div className="mb-4 md:mb-6">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Departman Bazlı Toplu Gönderim</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
                            {getDepartmentGroups(week).map((department: any) => (
                              <button
                                key={department.id}
                                className="flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-3 transition-colors disabled:opacity-50"
                                onClick={() => sendDepartmentHomeWork(week.id, department.id)}
                                disabled={homeworkSendLoading[`dept-${week.id}-${department.id}`]}>
                                {homeworkSendLoading[`dept-${week.id}-${department.id}`] ? (
                                  <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                                ) : (
                                  <>
                                    <Building className="w-4 h-4 text-blue-600" />
                                    <Send className="w-4 h-4 text-blue-600" />
                                  </>
                                )}
                                <span className="text-sm font-medium text-blue-700">
                                  {!homeworkSendLoading[`dept-${week.id}-${department.id}`] && `${department.name} Toplu Gönder`}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Hafta İçerikleri</h4>
                        <div className="space-y-3">
                          {week.items.map((item: any) => {
                            const content = item?.content || null;
                            const section = item?.section || null;
                            const department = item?.department || null;
                            const sectionTitleSafe = section?.title || "(Bölüm Bulunamadı)";
                            const contentTitleSafe = content?.title || "(İçerik Bulunamadı)";

                            const startDate = getDateOfWeek(week.week, week.year);
                            const endDate = new Date(startDate);
                            endDate.setDate(startDate.getDate() + 6);

                            return (
                              <div
                                key={item.id}
                                className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-200">
                                <div className="flex flex-col lg:flex-row items-stretch gap-4 lg:gap-6">
                                  <div className="flex lg:flex-col items-center lg:items-center gap-2 lg:gap-1.5 flex-shrink-0 lg:w-16 lg:justify-center">
                                    <div
                                      className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center ${getContentTypeBadgeClass(
                                        content?.type || ""
                                      )}`}>
                                      {getContentTypeIcon(content?.type || "")}
                                    </div>
                                    <span className="text-base lg:text-xs text-gray-600 font-medium text-center">
                                      {getContentTypeName(content?.type || "")}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0 flex flex-col justify-center py-1">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                                      <div className="flex items-center gap-2">
                                        <BookOpen className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                        <span className="text-gray-600 font-medium text-sm">Bölüm:</span>
                                        <span className="text-gray-900 font-medium text-sm">{sectionTitleSafe}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                                        <span className="text-gray-600 font-medium text-sm">İçerik:</span>
                                        <span className="text-gray-900 font-medium text-sm">{contentTitleSafe}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Building className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                        <span className="text-gray-600 font-medium text-sm">Departman:</span>
                                        <span
                                          className="text-white px-2 py-0.5 rounded font-medium text-sm"
                                          style={{
                                            backgroundColor: department?.color || "#6B7280",
                                          }}>
                                          {department?.name || "(Departman Bulunamadı)"}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                        <span className="text-gray-600 font-medium text-sm">Durum:</span>
                                        <span
                                          className={`px-2 py-0.5 rounded text-xs font-semibold ${item.status === "WAITING" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"
                                            }`}>
                                          {item.status === "WAITING" ? "Bekliyor" : "Gönderildi"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-stretch gap-2 w-full lg:w-auto lg:flex-shrink-0 lg:min-w-[160px] lg:justify-center">
                                    <button
                                      className={`px-5 py-2.5 rounded-lg font-semibold transition-colors text-sm flex items-center justify-center gap-2 ${homeworkSendLoading[`item-${item.id}`]
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : item.status === "WAITING"
                                          ? "bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md"
                                          : "bg-orange-600 hover:bg-orange-700 text-white shadow-sm hover:shadow-md"
                                        }`}
                                      onClick={() => sendItemHomeWork(week.id, item.id)}
                                      disabled={homeworkSendLoading[`item-${item.id}`]}>
                                      {homeworkSendLoading[`item-${item.id}`] ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <>
                                          <Send className="w-4 h-4" />
                                          <span>{item.status === "WAITING" ? "Gönder" : "Tekrar Gönder"}</span>
                                        </>
                                      )}
                                    </button>
                                    <div className="flex items-center gap-2">
                                      {hasCalendarDeletePermission && (
                                        <button
                                          onClick={() => handleDeleteItem(week.id, item.id)}
                                          disabled={item.status !== "WAITING"}
                                          className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5 font-medium ${item.status !== "WAITING"
                                            ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                                            : "text-red-600 hover:text-red-700 hover:bg-red-50"
                                            }`}
                                          title={item.status !== "WAITING" ? "Gönderilmiş ödevler silinemez" : "Öğeyi Sil"}>
                                          <Trash2 className="w-4 h-4" />
                                          <span className="text-sm">Sil</span>
                                        </button>
                                      )}
                                      <button
                                        onClick={() => {
                                          window.location.href = `/lessons/play/${lessonId}?contentId=${content?.id}`;
                                        }}
                                        className="flex-1 px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 font-medium"
                                        title="İçeriğe Git">
                                        <Eye className="w-4 h-4" />
                                        <span className="text-sm">Görüntüle</span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      ) : (
        <div className="bg-white rounded-lg p-8 text-center shadow-sm border border-gray-200">
          <div className="w-16 h-16 bg-purple-100 rounded-full mx-auto mb-6 flex items-center justify-center">
            <Calendar className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Akademik Takvim Boş</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Henüz hiç takvim öğesi eklenmemiş. Dersiniz için haftalık planlama oluşturmaya başlayın ve öğrencilerinize organize bir eğitim
            deneyimi sunun.
          </p>
          {hasCalendarAddPermission && (
            <button
              onClick={openModal}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors shadow-sm hover:shadow-md flex items-center gap-2 mx-auto">
              <Plus className="w-4 h-4" />
              İlk Takvim Öğesini Ekle
            </button>
          )}
        </div>
      )}

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Yeni Takvim Öğesi Ekle" size="md">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hafta Seçimi</label>
              <select
                value={selectedWeekYear}
                onChange={(e) => setSelectedWeekYear(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent">
                <option value="">Hafta seçiniz</option>
                {getWeekOptions().map((week) => (
                  <option key={week.value} value={week.value}>
                    {week.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Departmanlar
                <span className="text-xs text-gray-500 ml-2">(Çoklu Seçim Yapabilirsiniz)</span>
              </label>

              {selectedDepartments.length > 0 && (
                <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-sm font-medium text-green-900 mb-3">Seçili Departmanlar ({selectedDepartments.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedDepartments.map((departmentId) => {
                      const dept = selectedDepartmentDetails[departmentId];
                      return dept ? (
                        <span
                          key={departmentId}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm">
                          {dept.color && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />}
                          {dept.name}
                          <button
                            type="button"
                            onClick={() => handleRemoveDepartment(departmentId)}
                            className="text-green-600 hover:text-green-800 font-bold">
                            ×
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              <DepartmentSelector
                value=""
                onChange={handleDepartmentAdd}
                placeholder="Departman Seçin"
                excludeIds={selectedDepartments}
                allowClear={false}
                required={false}
                showColors={true}
                inline={true}
                className="text-sm"
                filterByUserResponsibility={true}
                lessonId={lessonId}
              />
            </div>

            {lesson?.sections && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Ders Bölümleri ve İçerikleri
                  <span className="text-xs text-gray-500 ml-2">(Bölüm Seçmek O Bölümün Tüm İçeriklerini Seçer)</span>
                </label>
                <LessonContentSelector
                  sections={lesson.sections}
                  selectedSections={selectedSections}
                  selectedContents={selectedContents}
                  onSectionToggle={handleSectionSelection}
                  onContentToggle={handleContentSelection}
                  className="text-sm"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors font-medium">
              İptal
            </button>
            <button
              onClick={handleAddCalendarItem}
              disabled={loading || !selectedWeekYear || selectedDepartments.length === 0 || selectedContents.length === 0}
              className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm hover:shadow-md">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                "Kaydet"
              )}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AcademicCalendar;